/**
 * Click-time dynamic import for heavy, rarely-used modules (PDF and Word
 * generators, export libraries).
 *
 * Why this exists: until 2026-10-06 the PDF toolkit (@react-pdf/renderer,
 * jspdf, docx) rode in the boot bundle, so a download button worked in any tab
 * however old. Loading it on click makes every page much lighter, but it adds
 * a failure that did not exist before: a tab opened BEFORE a deploy asks for a
 * chunk URL from the previous build. That file is gone, so the browser rejects
 * it ("Failed to fetch dynamically imported module", "Importing a module script
 * failed", depending on the browser). Until 2026-10-15 Vercel's SPA fallback
 * also answered the missing /assets path with index.html, which produced the
 * "… is not a valid JavaScript MIME type" texts; vercel.json now excludes
 * /assets/ from that rewrite so it is a clean 404, but the patterns stay for
 * tabs and caches from before.
 *
 * `importOnDemand(() => import('./x'))` retries once after ~800 ms, and if the
 * second attempt fails with a chunk-load error it throws a
 * {@link StaleBuildError}. Do not count on the retry to heal a network blip:
 * Chromium caches a FAILED module fetch in the document's module map, so the
 * second `import()` of the same URL rejects without touching the network
 * (verified on Chrome 154, 2026-10-06). The retry only helps in engines that
 * re-fetch (Safari/Firefox may), and in Chromium a one-off blip on first click
 * shows the "refresh" toast, which is still the right fix. `classify()` in errorMessages.ts maps that
 * to `stale_build`, whose toast tells the user Bloodstream was updated and to
 * refresh. Route-level code splitting keeps using `lazyWithRetry`, which
 * reloads the page instead (a click-time import must never reload under a
 * user who may have unsaved work on screen).
 *
 * 🚨 A click-time import is only safe if the chunk is already in this tab's
 * module map BEFORE the next deploy. main ships several times a day, and every
 * deploy re-hashes every generator chunk (they import the entry, whose name
 * changes with App), so a bare click-time import fails in nearly every tab
 * older than the last deploy — and Chromium caches that failure, so no retry
 * helps. Every module that calls `importOnDemand(() => import('./x'))` must
 * therefore ALSO call `warmOnIdle(() => import('./x'))` at its top level, with
 * the same specifier. That loads the generator at idle as soon as the module
 * holding the button is evaluated, i.e. from the same build, which is exactly
 * the guarantee the old static import gave, minus the cost on first paint.
 * Once loaded, later clicks resolve from the module map and a deploy cannot
 * break them. `importOnDemand` stays as the fallback for the narrow window
 * before idle fires.
 *
 * Deliberately self-contained (no import of errorMessages / Sentry): it is
 * imported from many surfaces, and must not drag anything into a bundle.
 */

/**
 * Marker for a StaleBuildError: its `name`. `isChunkLoadError` matches on
 * `name + message`, so errorMessages.ts recognises it without importing this
 * class. The MESSAGE is kept readable on purpose, because some older surfaces
 * (the Box upload buttons' in-session status line) still show a raw
 * `err.message`; the original browser error rides along as `cause`. What gets
 * STORED is different: see STALE_BUILD_STORED_MESSAGE.
 */
export const STALE_BUILD_MARKER = 'StaleBuildError'
const STALE_BUILD_MESSAGE = 'Bloodstream was updated since this page opened. Refresh the page and try again.'

/**
 * Text to STORE (e.g. `box_upload_error`) when a background job failed with a
 * StaleBuildError. Never store STALE_BUILD_MESSAGE: it is read back on a later,
 * freshly loaded page, where "refresh the page" is advice that cannot help.
 */
export const STALE_BUILD_STORED_MESSAGE =
  'The brief PDF was not generated because the page was out of date when the upload ran. Click Retry.'

/** True when `err` is the StaleBuildError thrown by {@link importOnDemand}. */
export function isStaleBuildError(err: unknown): boolean {
  return !!err && typeof err === 'object' && (err as { name?: unknown }).name === STALE_BUILD_MARKER
}

/** Browser texts for "this module chunk could not be loaded" (lower-cased). */
const CHUNK_LOAD_PATTERNS = [
  'failed to fetch dynamically imported module', // Chromium
  'importing a module script failed', // Safari
  'error loading dynamically imported module', // Firefox
  'is not a valid javascript mime type', // Safari, HTML served for a JS URL
  'expected a javascript module script', // Chromium, HTML served for a JS URL
  'chunkloaderror',
  'loading chunk', // "Loading chunk 123 failed"
  'unable to preload css', // Vite's preload helper, CSS dep of the chunk missing
  STALE_BUILD_MARKER.toLowerCase(),
]

/** True when `err` looks like a dynamic-import / chunk-load failure. */
export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false
  const name = typeof err === 'object' && 'name' in err ? String((err as { name?: unknown }).name ?? '') : ''
  const message =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message ?? '')
          : ''
  const text = `${name} ${message}`.toLowerCase()
  return CHUNK_LOAD_PATTERNS.some(p => text.includes(p))
}

/** A click-time import failed because this tab is running an older build than the server. */
export class StaleBuildError extends Error {
  constructor(original: unknown) {
    super(STALE_BUILD_MESSAGE)
    this.name = STALE_BUILD_MARKER
    // tsconfig targets ES2020 (no ErrorOptions), so assign `cause` after
    // construction. Sentry's LinkedErrors integration records it.
    ;(this as { cause?: unknown }).cause = original
  }
}

const RETRY_DELAY_MS = 800

/**
 * Import a module on demand, retrying once. A second chunk-load failure
 * becomes a {@link StaleBuildError}; any other failure (offline, or the module
 * throwing while it evaluates) is rethrown unchanged so it classifies as it
 * always did.
 */
export async function importOnDemand<T>(factory: () => Promise<T>): Promise<T> {
  try {
    return await factory()
  } catch {
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS))
    try {
      return await factory()
    } catch (second) {
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false
      if (!offline && isChunkLoadError(second)) throw new StaleBuildError(second)
      throw second
    }
  }
}

const WARM_FALLBACK_DELAY_MS = 1500
const WARM_IDLE_TIMEOUT_MS = 8000

/**
 * Load a click-time module in the background once the browser is idle, so it
 * comes from the same build as the code that will later ask for it. Call it at
 * the TOP LEVEL of the module that holds the `importOnDemand()` call, with the
 * same specifier (see the 🚨 note at the top of this file). Failures are
 * swallowed: the click still goes through `importOnDemand`, which reports them.
 * A no-op outside a browser (tests, SSR).
 */
export function warmOnIdle(...factories: Array<() => Promise<unknown>>): void {
  if (typeof window === 'undefined') return
  const run = () => {
    for (const factory of factories) {
      try {
        void factory().catch(() => {})
      } catch {
        // A factory that throws synchronously must not break module evaluation.
      }
    }
  }
  const ric = (window as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
    .requestIdleCallback
  if (typeof ric === 'function') ric.call(window, run, { timeout: WARM_IDLE_TIMEOUT_MS })
  else setTimeout(run, WARM_FALLBACK_DELAY_MS)
}
